import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ModalParagraph} from '@/components/ui/modal-atoms/ModalAtoms'
import {useTranslation} from '@/translations/i18n'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {getPatientServiceType, patientServiceTypeMapToLabel} from '@/utilities/helpers'
import {DialogStore} from '@ariakit/react'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {useCreatePatient} from '@/features/patients/services/useCreatePatient'
import {routes} from '@/utilities/constants/routes'
import {QUERY_KEYS} from '@/queryClient'
import {toast} from '@/utilities/toast/toast'
import {generatePath, useNavigate} from 'react-router-dom'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from '@/components/ui/button/Button'
import {AxiosError} from 'axios'
import {zodResolver} from '@hookform/resolvers/zod'
import {FormProvider, useForm} from 'react-hook-form'
import {CreatePatientFormValidationSchema} from '../../types'
import {remapPatientToPayload} from '../../utils'
import {HttpCreatePatientPayload} from '../../services/patients.http'

export const CreatePatientConfirmationModal = ({
    dialogStore,
    onErrorConfirmationModal,
    defaultValues
}: {
    dialogStore: DialogStore
    onErrorConfirmationModal: (errors: AxiosError<{errors: {field: string; rule: string}[]}>) => void
    defaultValues: CreatePatientFormValidationSchema
}) => {
    const {t} = useTranslation()

    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const form = useForm<CreatePatientFormValidationSchema>({
        resolver: zodResolver(CreatePatientFormValidationSchema),
        defaultValues
    })

    // mutations
    const {mutate: createPatientMutation, isPending: isPendingCreatePatient} = useCreatePatient({
        onSuccess: async patient => {
            if (patient.treatmentRequestEligibility.canRequest) {
                toast.success(
                    <Flexbox direction="column" gap={3} justify="space-between" align="flex-start" fullWidth>
                        {t('commons:has_been_added', {fullName: patient.fullName})}
                        <Button
                            shape="text"
                            variant="tertiary"
                            onClick={() => {
                                toast.dismiss('long-success-toast')
                                navigate({
                                    pathname: routes.CREATE_TREATMENT_REQUEST.handle.path,
                                    search: `?patientId=${patient.id}`
                                })
                            }}
                        >
                            {t('patients:create_treatment_request')}
                        </Button>
                    </Flexbox>,
                    {id: 'long-success-toast'}
                )
            } else {
                toast.success(t('commons:has_been_added', {fullName: patient.fullName}))
            }

            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS]})
            navigate(generatePath(routes.PATIENT_DETAILS_ROUTE.handle.path, {id: patient.id}))
        },
        onError: onErrorConfirmationModal
    })

    const handleConfirmSubmit = form.handleSubmit(
        formValues => createPatientMutation(HttpCreatePatientPayload.parse(remapPatientToPayload(formValues, true))),
        errors => console.error(errors)
    )

    const values = form.watch()

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={handleConfirmSubmit}
                isConfirmPending={isPendingCreatePatient}
                headerSlot={
                    <Flexbox direction="column">
                        <DialogHeading>{t('commons:add_patient')}</DialogHeading>
                        <ModalParagraph>
                            {values.lastName} {values.firstName} -{' '}
                            {t(patientServiceTypeMapToLabel[getPatientServiceType(values.birthDate).serviceType])}
                        </ModalParagraph>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
